/**
 * Rasterplan: die Felder des Untersuchungsgebiets, eingefaerbt nach ihrer
 * Einstufung.
 *
 * Reine Funktion, kein DOM, kein jsPDF.
 *
 * ═══ WARUM DIE FARBE AUS DEN DATEN KOMMEN MUSS ════════════════════════════
 * Im Musterplan ist der Lageplan mit den belasteten Feldern ein eingefuegtes
 * Bild aus dem Gutachten. Das ist der Normalfall — und der Grund fuer den
 * haeufigsten stillen Fehler dieser Plaene: kommt ein Befund hinzu oder
 * aendert sich eine Einstufung, aendert sich das Bild nicht mit. Es sieht
 * weiterhin richtig aus.
 *
 * Diese Skizze wird deshalb AUS DEN ERFASSTEN DATEN erzeugt. Sie ist kein
 * Ersatz fuer den vermessenen Lageplan (der bleibt als Anlage und kann als
 * Hintergrund hinterlegt werden), aber sie kann nicht veralten.
 *
 * ═══ WAS SIE UEBER SICH SELBST SAGT ═══════════════════════════════════════
 * Felder ohne Gitterlage koennen nicht gezeichnet werden. Sie verschwinden
 * NICHT, sondern werden unter der Zeichnung namentlich aufgefuehrt — sonst
 * liest sich ihre Abwesenheit als „gibt es nicht".
 */

import { ebvKlasse } from '../kataloge/ebvKlassen';
import { klassenLabel } from '../einstufung';
import type { Primitive, Zeichnung } from './primitive';

export interface RasterFeldDarstellung {
    feld_nr: string;
    spalte: number | null;
    zeile: number | null;
    einstufung: string | null;
    vorbehalt: boolean;
    /** Zu welchem Arbeitsbereich das Feld gehoert (Kennung), falls zugeordnet. */
    cluster_kennung?: string;
    /** Ob das Feld ausdruecklich als nicht A+S-pflichtig abgegrenzt ist. */
    abgegrenzt?: boolean;
}

export interface RasterParameter {
    titel?: string;
    /** Kantenlaenge einer Zelle in mm. */
    zelle_mm?: number;
    /** Spalten- und Zeilenbeschriftung anzeigen. */
    beschriftung?: boolean;
}

const RAND = 14;

export const zeichneRaster = (
    felder: RasterFeldDarstellung[],
    p: RasterParameter = {},
): Zeichnung => {
    const zelle = p.zelle_mm ?? 16;
    const teile: Primitive[] = [];

    const verortet = felder.filter(f => f.spalte !== null && f.zeile !== null);
    const ohneLage = felder.filter(f => f.spalte === null || f.zeile === null);

    if (verortet.length === 0) {
        return {
            breite: 120,
            hoehe: 40,
            titel: p.titel ?? 'Rasterplan',
            teile: [{
                art: 'text', x: RAND, y: RAND + 6,
                text: 'Kein Feld ist im Raster verortet, es kann nichts gezeichnet werden.',
                groesse: 8, anker: 'links', farbe: 'warnung',
            }],
            fussnoten: ohneLage.length > 0
                ? [`Ohne Gitterlage: ${ohneLage.map(f => f.feld_nr).join(', ')}`]
                : undefined,
        };
    }

    const maxSpalte = Math.max(...verortet.map(f => f.spalte as number));
    const maxZeile = Math.max(...verortet.map(f => f.zeile as number));
    const minSpalte = Math.min(...verortet.map(f => f.spalte as number));
    const minZeile = Math.min(...verortet.map(f => f.zeile as number));

    const x0 = RAND + (p.beschriftung === false ? 0 : 8);
    const y0 = RAND + (p.beschriftung === false ? 0 : 8);

    // ── Spalten- und Zeilenkoepfe ──────────────────────────────────────────
    if (p.beschriftung !== false) {
        for (let s = minSpalte; s <= maxSpalte; s++) {
            teile.push({
                art: 'text',
                x: x0 + (s - minSpalte) * zelle + zelle / 2,
                y: y0 - 2,
                text: String(s), groesse: 6, anker: 'mitte', farbe: 'linie',
            });
        }
        for (let z = minZeile; z <= maxZeile; z++) {
            teile.push({
                art: 'text',
                x: x0 - 2,
                y: y0 + (z - minZeile) * zelle + zelle / 2 + 1,
                text: String(z), groesse: 6, anker: 'rechts', farbe: 'linie',
            });
        }
    }

    // ── leeres Gitter ──────────────────────────────────────────────────────
    for (let s = minSpalte; s <= maxSpalte; s++) {
        for (let z = minZeile; z <= maxZeile; z++) {
            teile.push({
                art: 'rechteck',
                x: x0 + (s - minSpalte) * zelle,
                y: y0 + (z - minZeile) * zelle,
                b: zelle, h: zelle,
                fuellung: '#ffffff', linie: 'linie', strich: 'gestrichelt',
            });
        }
    }

    // ── belegte Felder ─────────────────────────────────────────────────────
    const benutzteKlassen = new Set<string>();
    for (const f of verortet) {
        const x = x0 + ((f.spalte as number) - minSpalte) * zelle;
        const y = y0 + ((f.zeile as number) - minZeile) * zelle;
        const k = ebvKlasse(f.einstufung);
        if (f.einstufung) benutzteKlassen.add(f.einstufung);

        teile.push({
            art: 'rechteck', x, y, b: zelle, h: zelle,
            fuellung: f.abgegrenzt ? '#ffffff' : (k?.farbe ?? '#e2e8f0'),
            linie: k?.belastet ? 'warnung' : 'linie_stark',
            strich: f.abgegrenzt ? 'gestrichelt' : 'voll',
        });
        teile.push({
            art: 'text', x: x + zelle / 2, y: y + zelle / 2 - 0.5,
            text: f.feld_nr, groesse: 7, fett: true, anker: 'mitte', farbe: 'text',
        });
        teile.push({
            art: 'text', x: x + zelle / 2, y: y + zelle / 2 + 4,
            text: f.abgegrenzt ? 'nicht A+S' : klassenLabel(f.einstufung, f.vorbehalt),
            groesse: 5.5, anker: 'mitte', farbe: f.abgegrenzt ? 'linie' : 'text',
        });
        if (f.cluster_kennung) {
            teile.push({
                art: 'text', x: x + 1.5, y: y + 4,
                text: f.cluster_kennung, groesse: 5.5, fett: true, anker: 'links', farbe: 'hinweis',
            });
        }
    }

    const breite = x0 + (maxSpalte - minSpalte + 1) * zelle + RAND;
    const hoehe = y0 + (maxZeile - minZeile + 1) * zelle + RAND;

    const fussnoten: string[] = [
        'Schematische Darstellung aus den erfassten Befunden. Sie ersetzt den vermessenen '
        + 'Lageplan des Gutachters nicht, veraltet aber auch nicht.',
    ];
    if (ohneLage.length > 0) {
        fussnoten.push(
            `Ohne Gitterlage und deshalb nicht dargestellt: ${ohneLage.map(f => f.feld_nr).join(', ')}.`,
        );
    }

    return {
        breite,
        hoehe,
        titel: p.titel ?? 'Rasterplan mit Einstufung der Felder',
        teile,
        legende: [...benutzteKlassen]
            .map(k => ebvKlasse(k))
            .filter((k): k is NonNullable<ReturnType<typeof ebvKlasse>> => !!k)
            .sort((a, b) => a.rang - b.rang)
            .map(k => ({ farbe: k.farbe, text: `${k.label}: ${k.legende}` })),
        fussnoten,
    };
};
